import type { BuildPhase } from "@hatch/shared";
import { BUILD_PHASES, PHASE_ORDER } from "@hatch/shared";
import { PHASE_LABELS } from "../../lib/labels.js";

/**
 * Where the build is. Only ever advances — the reducer refuses a regression,
 * because a timeline that jumps backwards reads as failure even when the build
 * is healthy.
 *
 * Sized to sit in the header rail: dense enough to stay on one line, quiet
 * enough that it does not compete with the panes it labels.
 */
export function PhaseTimeline({ phase, failed }: { phase: BuildPhase; failed: boolean }) {
  const currentIndex = PHASE_ORDER[phase];
  // `queued` is bookkeeping, not something the user watches happen.
  const visible = BUILD_PHASES.filter((p) => p !== "queued");

  return (
    <ol className="flex flex-wrap items-center gap-x-1 gap-y-1" aria-label="Build progress">
      {visible.map((p, i) => {
        const index = PHASE_ORDER[p];
        const done = index < currentIndex;
        const active = index === currentIndex;

        return (
          <li key={p} className="flex items-center gap-1">
            <span
              className={[
                "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors",
                active && !failed
                  ? "bg-accent-soft font-medium text-accent"
                  : done
                    ? "text-ink-soft"
                    : "text-ink-faint",
              ].join(" ")}
              aria-current={active ? "step" : undefined}
            >
              <span
                aria-hidden
                className={[
                  "size-1.5 rounded-full",
                  failed && active
                    ? "bg-halt"
                    : active
                      ? "bg-accent animate-pulse-dot"
                      : done
                        ? "bg-done"
                        : "bg-rule-strong",
                ].join(" ")}
              />
              {PHASE_LABELS[p]}
            </span>
            {i < visible.length - 1 && <span aria-hidden className="h-px w-3 bg-rule sm:w-5" />}
          </li>
        );
      })}
    </ol>
  );
}
